import { RoleGate } from '@/components/RoleGate';
import { UnsavedChangesBar } from '@/components/ui/UnsavedChangesBar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LabelsManager } from '@/features/projects/components/LabelsManager';
import { ProjectDangerZone } from '@/features/projects/components/ProjectDangerZone';
import { StatesManager } from '@/features/projects/components/StatesManager';
import { useAnalyzeText } from '@/features/projects/hooks/useAnalyzeText';
import { useUpdateProject } from '@/features/projects/hooks/useUpdateProject';
import { AiSuggestionPanel } from '@/features/work-items/components/AiSuggestionPanel';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useFlag } from '@/lib/hooks/useFlag';
import { type UpdateProjectInput, updateProjectSchema } from '@/lib/schemas';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Loader2, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

/** Mensaje amable segun el codigo de error de analyze-text. */
function analyzeErrorMessage(code: string): string {
  if (code === 'service_unavailable') return 'La IA no está configurada en el servidor.';
  if (code === 'upstream_error') return 'El servicio de IA no respondió. Prueba de nuevo.';
  if (code === 'forbidden') return 'No tienes permiso para usar esta función.';
  return 'No se pudo analizar la descripción.';
}

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug/settings')({
  component: ProjectSettingsPage,
});

// Slug intentionally omitted — changing it breaks shared links.
const formSchema = updateProjectSchema.pick({
  name: true,
  description: true,
  icon: true,
  color: true,
  due_warning_days: true,
  require_completion_comment: true,
});
type FormValues = Pick<
  UpdateProjectInput,
  'name' | 'description' | 'icon' | 'color' | 'due_warning_days' | 'require_completion_comment'
>;

function ProjectSettingsPage() {
  const { wsSlug, projectSlug } = useParams({
    from: '/_app/$wsSlug/projects/$projectSlug/settings',
  });

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projQ = useQuery({
    ...getProjectBySlugQuery(wsQ.data?.id ?? '', projectSlug),
    enabled: !!wsQ.data?.id,
  });

  const update = useUpdateProject();
  const { user } = useAuth();
  const aiEnabled = useFlag('ai_analyze_detail', wsQ.data?.id, user?.id);
  const analyze = useAnalyzeText();
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      icon: '',
      color: '',
      due_warning_days: 3,
      require_completion_comment: false,
    },
  });

  // Sync form when project loads.
  useEffect(() => {
    if (!projQ.data) return;
    const project = projQ.data;
    form.reset({
      name: project.name ?? '',
      description: project.description ?? '',
      icon: project.icon ?? '',
      color: project.color ?? '',
      due_warning_days: project.due_warning_days ?? 3,
      require_completion_comment: project.require_completion_comment ?? false,
    });
  }, [projQ.data, form]);

  if (wsQ.isPending || (wsQ.data && projQ.isPending)) {
    return <div className="p-6 text-sm text-quiet">Cargando proyecto...</div>;
  }
  if (!wsQ.data || !projQ.data) {
    return (
      <div className="p-6 text-sm text-quiet">
        Proyecto no encontrado o sin permisos para verlo.
      </div>
    );
  }
  const ws = wsQ.data;
  const project = projQ.data;

  const onSubmit = form.handleSubmit(async (values) => {
    // Normalize empty strings to null for nullable fields.
    const patch: UpdateProjectInput = {
      name: values.name?.trim() || undefined,
      description: values.description?.trim() ? values.description : null,
      icon: values.icon?.trim() ? values.icon : null,
      color: values.color?.trim() ? values.color : null,
      due_warning_days: values.due_warning_days,
      require_completion_comment: values.require_completion_comment ?? false,
    };
    await update.mutateAsync({
      projectId: project.id,
      workspaceId: ws.id,
      patch,
    });
    form.reset(values);
  });

  function onAnalyzeWithAi() {
    const text = (form.getValues('description') ?? '').trim();
    if (!text) {
      toast.info('Escribe una descripción primero para analizarla.');
      return;
    }
    const payload = text.length > 8000 ? text.slice(0, 8000) : text;
    if (text.length > 8000) toast.info('La descripción es larga: se analizará el comienzo.');
    analyze.mutate(
      { text: payload, title: (form.getValues('name') ?? '').trim() || undefined },
      {
        onSuccess: (suggestion) => setAiSuggestion(suggestion),
        onError: (err) => toast.error(analyzeErrorMessage(err instanceof Error ? err.message : '')),
      },
    );
  }

  return (
    <RoleGate
      wsSlug={wsSlug}
      message="La configuración del proyecto solo está disponible para administradores y propietarios del workspace."
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border-soft flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink">{project.name}</h2>
            <p className="text-sm text-quiet mt-0.5">
              Configuración del proyecto: nombre, color, estados y etiquetas. Los cambios afectan a
              todos los miembros del proyecto.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-6 max-w-2xl space-y-10">
          {/* Project basic info */}
          <section>
            <h3 className="text-sm font-semibold text-ink mb-3">Información del proyecto</h3>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="proj-name">Nombre</Label>
                <Input
                  id="proj-name"
                  placeholder="Ej: Servicio al Cliente, Implementación SAP"
                  {...form.register('name')}
                  aria-invalid={!!form.formState.errors.name}
                />
                {form.formState.errors.name && (
                  <p className="text-xs text-critical">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor="proj-description">Descripción</Label>
                  {aiEnabled && (
                    <button
                      type="button"
                      onClick={onAnalyzeWithAi}
                      disabled={analyze.isPending}
                      title="Mejora la redacción con IA. Se envía el texto a OpenAI para procesarlo."
                      aria-label="Analizar y mejorar la descripción con IA"
                      className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border bg-surface-elev text-quiet text-xs font-medium hover:text-accent hover:border-accent transition-colors disabled:cursor-not-allowed disabled:opacity-80"
                    >
                      {analyze.isPending ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Sparkles size={13} />
                      )}
                      <span>{analyze.isPending ? 'Analizando…' : 'Analizar con IA'}</span>
                    </button>
                  )}
                </div>
                <textarea
                  id="proj-description"
                  rows={4}
                  {...form.register('description')}
                  placeholder="Describe el alcance, sponsor, objetivos..."
                  className="w-full resize-y bg-surface-elev border border-border rounded-md p-2 text-sm text-body outline-none focus:border-accent"
                />
                {form.formState.errors.description && (
                  <p className="text-xs text-critical">
                    {form.formState.errors.description.message}
                  </p>
                )}
                {aiSuggestion !== null && (
                  <AiSuggestionPanel
                    suggestion={aiSuggestion}
                    format="text"
                    onUse={() => {
                      form.setValue('description', aiSuggestion, { shouldDirty: true });
                      setAiSuggestion(null);
                      toast.success('Descripción actualizada con la sugerencia.');
                    }}
                    onDiscard={() => setAiSuggestion(null)}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="proj-icon">
                    Icono <span className="text-mute font-normal">(emoji opcional)</span>
                  </Label>
                  <IconPicker
                    value={form.watch('icon') ?? ''}
                    onChange={(v) =>
                      form.setValue('icon', v, { shouldDirty: true, shouldValidate: true })
                    }
                  />
                  {form.formState.errors.icon && (
                    <p className="text-xs text-critical">{form.formState.errors.icon.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="proj-color">
                    Color <span className="text-mute font-normal">(hex)</span>
                  </Label>
                  <ColorPicker
                    value={form.watch('color') ?? ''}
                    onChange={(v) =>
                      form.setValue('color', v, { shouldDirty: true, shouldValidate: true })
                    }
                    invalid={!!form.formState.errors.color}
                  />
                  {form.formState.errors.color && (
                    <p className="text-xs text-critical">{form.formState.errors.color.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="proj-warn">Aviso de vencimiento</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="proj-warn"
                    type="number"
                    min={0}
                    max={90}
                    className="w-24"
                    {...form.register('due_warning_days', {
                      setValueAs: (v) =>
                        v === '' || v == null ? 3 : Math.max(0, Math.min(90, Number(v))),
                    })}
                    aria-invalid={!!form.formState.errors.due_warning_days}
                  />
                  <span className="text-sm text-quiet">días antes del vencimiento</span>
                </div>
                <p className="text-xs text-mute">
                  Las tareas se marcan en amarillo cuando entran en este rango antes del{' '}
                  <code className="font-mono">due_date</code>, y en rojo si ya están vencidas. Pon{' '}
                  <code className="font-mono">0</code> si solo quieres ver las vencidas.
                </p>
                {form.formState.errors.due_warning_days && (
                  <p className="text-xs text-critical">
                    {form.formState.errors.due_warning_days.message}
                  </p>
                )}
              </div>

              {/* Comentario de cierre obligatorio: default del proyecto.
                  Override por tarea via work_items.require_completion_comment. */}
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="proj-req-closing">Comentario de cierre obligatorio</Label>
                    <p className="text-xs text-mute mt-0.5">
                      Cuando esté activo, el ejecutor deberá escribir un comentario (mínimo 20
                      caracteres) al mover una tarea a un estado completado. Se puede desactivar por
                      tarea al crearla.
                    </p>
                  </div>
                  <button
                    id="proj-req-closing"
                    type="button"
                    role="switch"
                    aria-checked={!!form.watch('require_completion_comment')}
                    onClick={() =>
                      form.setValue(
                        'require_completion_comment',
                        !form.watch('require_completion_comment'),
                        { shouldDirty: true },
                      )
                    }
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-sf)]',
                      form.watch('require_completion_comment')
                        ? 'bg-[color:var(--accent)]'
                        : 'bg-border',
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-card transition-transform',
                        form.watch('require_completion_comment')
                          ? 'translate-x-4'
                          : 'translate-x-0',
                      )}
                    />
                  </button>
                </div>
              </div>

              <div className="space-y-1 opacity-60">
                <Label>Identifier</Label>
                <Input value={project.identifier} disabled readOnly />
                <p className="text-xs text-mute">
                  El identificador (prefijo de las tareas, ej. {project.identifier}-12) no se puede
                  cambiar.
                </p>
              </div>

              <div className="space-y-1 opacity-60">
                <Label>Slug (URL)</Label>
                <Input value={project.slug} disabled readOnly />
                <p className="text-xs text-mute">
                  Editar el slug rompe los enlaces compartidos. Si necesitas cambiarlo, pídelo al
                  administrador del workspace.
                </p>
              </div>

              {/* Submit oculto: el control visible (Guardar/Descartar) vive en
                  la barra sticky al pie, que llama onSubmit() directo. Este
                  boton solo existe para conservar el submit nativo con Enter
                  desde los campos del form. */}
              <button type="submit" hidden aria-hidden tabIndex={-1} />
            </form>
          </section>

          <hr className="border-border" />

          {/* States section */}
          <StatesManager projectId={project.id} workspaceId={ws.id} />

          <hr className="border-border" />

          {/* Labels section */}
          <LabelsManager projectId={project.id} workspaceId={ws.id} />

          <hr className="border-border" />

          {/* Danger zone: archive + hard delete */}
          <ProjectDangerZone
            projectId={project.id}
            projectName={project.name}
            workspaceId={ws.id}
            workspaceSlug={wsSlug}
          />
        </div>

        {/* Barra sticky de cambios sin guardar. Vive como hermana del area
            scrollable (flex-shrink-0), asi queda fija al pie sin importar el
            scroll dentro del form. */}
        <UnsavedChangesBar
          className="flex-shrink-0"
          isDirty={form.formState.isDirty}
          isSaving={update.isPending}
          onSave={() => void onSubmit()}
          onDiscard={() => form.reset()}
        />
      </div>
    </RoleGate>
  );
}

const ICON_PRESETS = [
  '🚀',
  '⭐',
  '💎',
  '🔥',
  '⚡',
  '🎯',
  '🛟',
  '🛠️',
  '🔧',
  '⚙️',
  '📦',
  '📊',
  '📈',
  '📉',
  '💼',
  '📋',
  '🗂️',
  '📁',
  '🧩',
  '🎨',
  '✨',
  '💡',
  '🧪',
  '🔬',
  '🛡️',
  '🏗️',
  '🌐',
  '☁️',
  '🤖',
  '📡',
  '💻',
  '⚛️',
  '🎮',
  '🎬',
  '🎵',
  '🍎',
  '🌱',
  '🌍',
  '✅',
  '❤️',
];

function IconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full h-9 rounded-md border border-border bg-surface px-2',
          'flex items-center justify-between gap-2 text-left',
          'hover:border-mute focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-sf)]',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="text-lg leading-none">
          {value || <span className="text-mute text-sm">Elegir icono</span>}
        </span>
        <span className="text-mute text-xs">{value ? 'Cambiar' : '▾'}</span>
      </button>

      {open ? (
        <div
          // biome-ignore lint/a11y/useSemanticElements: anchored popover
          role="dialog"
          aria-label="Seleccionar icono"
          className="absolute left-0 top-full mt-1 z-30 w-[280px] rounded-lg border border-border bg-surface shadow-pop p-2"
        >
          <div className="grid grid-cols-8 gap-1">
            {ICON_PRESETS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onChange(emoji);
                  setOpen(false);
                }}
                className={cn(
                  'h-8 w-8 inline-flex items-center justify-center rounded text-lg transition-colors',
                  value === emoji ? 'bg-accent-sf' : 'hover:bg-surface-elev',
                )}
                aria-label={`Icono ${emoji}`}
                aria-pressed={value === emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-border-soft flex items-center gap-2">
            <Input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="O escribe un emoji"
              maxLength={20}
              className="h-8 text-sm"
            />
            {value ? (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs text-quiet hover:text-critical px-1.5 py-1 rounded"
              >
                Quitar
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const COLOR_PRESETS = [
  '#3B5BDB', // accent / indigo
  '#7C3AED', // accent-2 / violet
  '#0EA5E9', // cat-info / cyan
  '#10B981', // cat-success / green
  '#F59E0B', // cat-warning / amber
  '#EF4444', // cat-critical / red
  '#EC4899', // pink
  '#64748B', // slate
];

function ColorPicker({
  value,
  onChange,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  const normalized = (value || '').trim();
  const isPreset = COLOR_PRESETS.some((c) => c.toLowerCase() === normalized.toLowerCase());

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : '#3B5BDB'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-9 w-12 shrink-0 rounded-md border border-border bg-surface cursor-pointer p-0.5"
          aria-label="Selector de color"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3B5BDB"
          className="font-mono text-sm"
          aria-invalid={invalid}
        />
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {COLOR_PRESETS.map((c) => {
          const active = normalized.toLowerCase() === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={cn(
                'h-6 w-6 rounded-full border transition-all',
                active
                  ? 'border-ink ring-2 ring-offset-1 ring-[color:var(--accent-sf)]'
                  : 'border-border hover:scale-110',
              )}
              style={{ backgroundColor: c }}
              aria-label={`Color ${c}`}
              aria-pressed={active}
              title={c}
            />
          );
        })}
        {!isPreset && /^#[0-9A-Fa-f]{6}$/.test(normalized) ? (
          <span
            aria-hidden
            className="h-6 w-6 rounded-full border border-ink ring-2 ring-offset-1 ring-[color:var(--accent-sf)]"
            style={{ backgroundColor: normalized }}
            title={`Personalizado: ${normalized}`}
          />
        ) : null}
      </div>
    </div>
  );
}
